/* @jsx jsx */
import { jsx } from '@theme-ui/core';
import * as React from 'react';
import { SliderState, SliderStateOptions, useSliderState } from '@react-stately/slider';
import { useFocusRing } from '@react-aria/focus';
import { VisuallyHidden } from '@react-aria/visually-hidden';
import { mergeProps } from '@react-aria/utils';
import { useNumberFormatter } from '@react-aria/i18n';
import { SliderThumbOptions, useSlider as useAriaSlider, useSliderThumb } from '@react-aria/slider';
import { AriaSliderProps } from '@react-types/slider';
import { NumberFormatOptions } from '@internationalized/number';
import { Stack } from './Stack';
import { Inline } from './Inline';

interface Props extends SliderOptions {}

interface SliderOptions
  extends Omit<AriaSliderProps, 'numberFormatter'>,
    Omit<SliderStateOptions, 'numberFormatter'> {
  numberFormatter?: Intl.NumberFormat;
  formatOptions?: NumberFormatOptions;
}

export function useSlider({ numberFormatter: numberFormatterProp, ...props }: SliderOptions) {
  let trackRef = React.useRef(null);
  let defaultNumberFormatter = useNumberFormatter(props.formatOptions);
  const numberFormatter = numberFormatterProp ?? defaultNumberFormatter;
  let state = useSliderState({ ...props, numberFormatter });
  let { groupProps, trackProps, labelProps, outputProps } = useAriaSlider(props, state, trackRef);

  return {
    groupProps,
    trackProps,
    labelProps,
    outputProps,
    trackRef,
    numberFormatter,
    formattedMinValue: numberFormatter.format(props.minValue || 0),
    formattedMaxValue: props.maxValue ? numberFormatter.format(props.maxValue) : null,
    state
  };
}

export function Slider(props: Props) {
  let {
    groupProps,
    trackProps,
    labelProps,
    outputProps,
    state,
    formattedMinValue,
    formattedMaxValue,
    trackRef
  } = useSlider(props);

  const thumbPercent = Number.parseInt(`${state.getThumbPercent(0) * 100}`);

  return (
    <Stack
      {...groupProps}
      sx={{
        position: 'relative',
        touchAction: 'none'
      }}
      gap="2rem"
    >
      {/* Create a flex container for the label and output element. */}
      <div>{props.label && <label {...labelProps}>{props.label}</label>}</div>
      <Inline alignY="center" gap="3">
        <span sx={{ variant: 'layout.budget.minMax' }}>{formattedMinValue}</span>
        {/* The track element holds the visible track line and the thumb. */}
        <div
          {...trackProps}
          ref={trackRef}
          sx={{
            position: 'relative',
            height: 30,
            width: '100%'
          }}
        >
          <div
            sx={{
              position: 'absolute',
              height: 3,
              top: 13,
              width: '100%',
              background: `linear-gradient(91deg, var(--color-primary) ${thumbPercent}%, var(--color-gray400) ${thumbPercent}%)`
            }}
          />
          <Thumb index={0} state={state} trackRef={trackRef} outputProps={outputProps} />
        </div>
        <span sx={{ variant: 'layout.budget.minMax' }}>{formattedMaxValue}+</span>
      </Inline>
    </Stack>
  );
}

interface ThumbProps extends Pick<SliderThumbOptions, 'trackRef' | 'index'> {
  state: SliderState;
  outputProps: React.OutputHTMLAttributes<HTMLOutputElement>;
}

function Thumb(props: ThumbProps) {
  let { state, trackRef, index, outputProps } = props;
  let inputRef = React.useRef(null);
  let { thumbProps, inputProps } = useSliderThumb(
    {
      index,
      trackRef,
      inputRef
    },
    state
  );

  let { focusProps, isFocusVisible } = useFocusRing();
  const thumbPercent = state.getThumbPercent(index);
  return (
    <Stack
      align="center"
      sx={{
        position: 'absolute',
        top: '4px',
        transform: 'translate(-50%, -50%)',
        left: `${thumbPercent * 100}%`
      }}
    >
      <output
        {...outputProps}
        sx={{
          visibility: thumbPercent === 0 || thumbPercent === 1 ? 'hidden' : undefined,
          variant: 'layout.budget.budgetPrice'
        }}
      >
        {state.getThumbValueLabel(0)}
      </output>
      <div
        {...thumbProps}
        sx={{
          width: '20px',
          height: '20px',
          borderRadius: '50%',
          bg: state.isThumbDragging(index) ? 'secondary' : 'primary',
          outlineOffset: '2px',
          outline: isFocusVisible ? 'auto 2px var(--color-primary)' : undefined,
          variant: 'layout.budget.budgetCircle'
        }}
      >
        <VisuallyHidden>
          <input ref={inputRef} {...mergeProps(inputProps, focusProps)} />
        </VisuallyHidden>
      </div>
    </Stack>
  );
}
